import React, {ReactElement, useEffect, useRef, useState} from 'react';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {
  CUSTOM_PAGINATION_SIZE_OPTIONS,
  PAGINATION_SIZE
} from 'src/constants/SystemConstants';
import {APP, DEFAULT_BLACK_COLOR} from 'src/style/variable';
import {useTranslation} from 'react-i18next';
import {Link, Button} from '@material-ui/core';
import {axios, CancelTokenSource} from '@shared/axios';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {getPatientAllInsuranceDetails} from '@shared/services/InsuranceEligibilityServices';
import {useSelector} from 'react-redux';
import HelperUtils from '@shared/utils/HelperUtils';
import './InsuranceHistoryContainerStyles.scss';
import ViewInsuranceImagesCardsDialog from './ViewInsuranceImagesDialog';
import Loader from '@components/framework/loader/Loader';
import {
  EligibilityStatusMapping,
  EligibilityStatusObj,
  INSURANCE_SOURCE_MAPPER
} from '@shared/constants/Mapping';
import {State} from '@shared/store/reducers/Index';
import VerifyInsuranceModal from '../../../Patients/Patientdetails/EligibilityVerification/VerifyInsuranceModal';
import {APP_PRIMARY_COLOR} from 'src/style/variable';

interface stateComponentInterface {
  viewInsuranceDialogOpen: boolean;
  selectedInsurance: any;
  addEditInsuranceModalOpen: boolean;
  addEditInsuranceInitialValues: any;
  addEditMode: 'add' | 'edit';
  loader: boolean;
  activeInsurances: Array<[]>;
  otherInsurances: Array<[]>;
  fromEdit: boolean;
}
const InsuranceHistoryContainer = (): ReactElement => {
  const activeInsuranceTableRef: any = React.useRef();
  const pastInsuranceTableRef: any = React.useRef();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const groupId: any = useSelector((state: State) => state.auth.groupId);
  const patientDetails = useSelector(
    (state: any) => state.patientDetailsDrawer.patientDetails
  );
  const insuranceReq = useRef<CancelTokenSource | null>(null);

  // use state for opening dialogs
  const clinicId = useSelector(
    (state: State) => state.patientDetailsDrawer?.clinicId
  );
  const [
    insuranceHistoryState,
    setInsuranceHistoryState
  ] = useState<stateComponentInterface>({
    viewInsuranceDialogOpen: false,
    selectedInsurance: null,
    addEditInsuranceModalOpen: false,
    addEditInsuranceInitialValues: null,
    addEditMode: 'add',
    loader: false,
    activeInsurances: [],
    otherInsurances: [],
    fromEdit: false
  });
  const {
    viewInsuranceDialogOpen,
    selectedInsurance,
    addEditInsuranceModalOpen,
    addEditInsuranceInitialValues,
    addEditMode,
    loader,
    activeInsurances,
    otherInsurances,
    fromEdit
  } = insuranceHistoryState;

  const eligibilityStatusMapping = EligibilityStatusMapping.map(item => {
    item.name = t(item.name);
    return item;
  });

  const getEligibilityStatusLabel = (status: string) => {
    const [eligibilityStatus] = eligibilityStatusMapping.filter(
      item => item.id == status
    );
    if (eligibilityStatus) {
      return eligibilityStatus.name;
    } else {
      return status;
    }
  };

  const fetchPatientInsuranceHistory: any = async (
    fetchSize?: number,
    offset?: number,
    sorting?: {sort: string; order: string}
  ) => {
    try {
      setInsuranceHistoryState(p => ({
        ...p,
        loader: true
      }));
      const options: any = {};
      if (fetchSize) {
        options.limit = fetchSize;
      }
      if (offset && offset >= 0) {
        options.offset = offset;
      }

      if (insuranceReq.current) {
        insuranceReq.current.cancel();
      }
      insuranceReq.current = axios.CancelToken.source();
      const response = await getPatientAllInsuranceDetails(
        groupId,
        patientDetails?.patient_id,
        insuranceReq.current
      );
      if (response?.data?.rows) {
        const activeInsuranceList = response.data.rows.filter(
          (insurance: any) => insurance.status === EligibilityStatusObj.ACTIVE
        );
        const prevInsuranceList = response.data.rows.filter(
          (insurance: any) => insurance.status !== EligibilityStatusObj.ACTIVE
        );
        setInsuranceHistoryState(p => ({
          ...p,
          loader: false,
          activeInsurances: activeInsuranceList,
          otherInsurances: prevInsuranceList
        }));
      }
    } catch (err: any) {
      enqueueSnackbar(
        (err && err.message) || t('PATIENTS.GET_ALL_PATIENTS_ERROR'),
        SnackBarConfig('e')
      );
      throw err;
    } finally {
      setInsuranceHistoryState(p => ({
        ...p,
        loader: false
      }));
    }
  };

  const getInsuranceSourceLabel = (source: string) => {
    if (source) {
      if (source in INSURANCE_SOURCE_MAPPER)
        return t(
          INSURANCE_SOURCE_MAPPER[
            source as keyof typeof INSURANCE_SOURCE_MAPPER
          ].label
        );
      else return source;
    }
    return '-';
  };

  // NOTE: add the insurance card types here if in future if something new comes up here
  const getInsuranceType = (key: string): string => {
    const insuranceTypes: {[key: string]: string} = {
      regular_insurance: t('ELIGIBILITY_VERIFICATION.REGULAR_INSURANCE'),
      pharmacy_benefit_card: t('ELIGIBILITY_VERIFICATION.PHARMACY_BENEFIT_CARD')
    };
    return insuranceTypes[key];
  };

  const viewInsuranceCardsDialog = (data: any) => {
    setInsuranceHistoryState(prevState => ({
      ...prevState,
      viewInsuranceDialogOpen: true,
      selectedInsurance: data,
      fromEdit: false
    }));
  };

  const closeViewInsuranceCardsDialog = () => {
    setInsuranceHistoryState(prevState => ({
      ...prevState,
      viewInsuranceDialogOpen: false,
      selectedInsurance: null,
      fromEdit: false
    }));
  };

  const openAddInsuranceModal = () => {
    setInsuranceHistoryState(prevState => ({
      ...prevState,
      addEditInsuranceModalOpen: true,
      addEditInsuranceInitialValues: null,
      addEditMode: 'add'
    }));
  };

  const openEditInsuranceModal = (rowData: any) => {
    setInsuranceHistoryState(prevState => ({
      ...prevState,
      addEditInsuranceModalOpen: true,
      addEditInsuranceInitialValues: {
        id: rowData?.id,
        payer_name: rowData?.payer_name,
        subscriber_group_no: rowData?.subscriber_group_no,
        subscriber_member_no: rowData?.subscriber_member_no,
        type: rowData?.insurance_type,
        bin_number: rowData?.bin_number,
        process_control_number: rowData?.process_control_number,
        priority: rowData?.priority,
        rx_group_no: rowData?.rx_group_no
      },
      addEditMode: 'edit'
    }));
  };

  const closeAddEditInsuranceModal = () => {
    setInsuranceHistoryState(prevState => ({
      ...prevState,
      addEditInsuranceModalOpen: false,
      addEditInsuranceInitialValues: null,
      addEditMode: 'add'
    }));
  };

  const columns = [
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.TITLE'),
      field: 'payer_name',
      render: (rowData: any) => {
        return <div>{rowData?.payer_name || '-'}</div>;
      }
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.PRIORITY'),
      field: 'priority',
      render: (rowData: any) => (
        <div>{HelperUtils.captilizeFirstLetter(rowData?.priority) || '-'}</div>
      )
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.INSURANCE_TYPE'),
      field: 'insurance_type',
      render: (rowData: any) => (
        <div>{getInsuranceType(rowData?.insurance_type) || '-'}</div>
      )
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.START_DATE'),
      field: 'start_date',
      render: (rowData: any) => (
        <div>
          {rowData?.start_date
            ? HelperUtils.formatDisplayDate(rowData?.start_date) || '-'
            : '-'}
        </div>
      )
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.END_DATE'),
      field: 'end_date',
      render: (rowData: any) => (
        <div>
          {rowData?.end_date
            ? HelperUtils.formatDisplayDate(rowData?.end_date) || '-'
            : '-'}
        </div>
      )
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.SOURCE'),
      field: 'source',
      render: (rowData: any) => {
        return <div>{getInsuranceSourceLabel(rowData?.source)}</div>;
      }
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.LAST_VERIFIED_ON'),
      field: 'last_verified_on',
      render: (rowData: any) => (
        <div>
          {HelperUtils.formatDisplayDate(rowData?.last_verified_on) || '-'}
        </div>
      )
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.STATUS'),
      field: 'status',
      render: (rowData: any) => (
        <div>{getEligibilityStatusLabel(rowData?.status) || '-'}</div>
      )
    },
    {
      title: t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.ACTIONS'),
      field: 'action',
      render: (rowData: any) => (
        <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
          <Link
            onClick={() => {
              viewInsuranceCardsDialog(rowData);
            }}>
            {t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.VIEW')}
          </Link>
          <Link
            onClick={() => {
              openEditInsuranceModal(rowData);
            }}
            style={{color: APP_PRIMARY_COLOR, cursor: 'pointer'}}>
            {t('COMMON.EDIT')}
          </Link>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchPatientInsuranceHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetails, groupId]);

  return (
    <div className="insurance-history-container">
      {loader ? (
        <Loader />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
            <span />
            <Button
              variant="contained"
              color="primary"
              onClick={openAddInsuranceModal}
              disabled={!patientDetails?.patient_id || !groupId}>
              {t('PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.ADD_INSURANCE')}
            </Button>
          </div>
          {/* Active Insurances Table */}
          <div className="insurance-history-parent">
            {t(
              'PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.ACTIVE_INSURANCE_TITLE'
            )}
          </div>
          <GridTable
            tableRef={activeInsuranceTableRef}
            columns={columns}
            data={activeInsurances}
            options={{
              ...DEFAULT_GRID_TABLE__OPTIONS,
              search: false,
              showTitle: false,
              toolbar: false,
              sorting: true,
              paging: true,
              emptyRowsWhenPaging: false,
              pageSize: PAGINATION_SIZE.ACTION_HISTORY,
              pageSizeOptions: CUSTOM_PAGINATION_SIZE_OPTIONS.ACTION_HISTORY,
              showEmptyDataSourceMessage: true,
              maxBodyHeight: '100%',
              tableLayout: 'auto',
              selection: false,
              draggable: false,
              headerStyle: {
                color: DEFAULT_BLACK_COLOR,
                fontFamily: APP.FONT_FAMILY.MEDIUM,
                flexDirection: 'row'
              }
            }}
            localization={{
              body: {
                emptyDataSourceMessage: t(
                  'PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.NO_ACTIVE_INSURANCE'
                )
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              boxShadow:
                '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
              flexShrink: 0,
              maxHeight: '30rem'
            }}
          />
          <div className="insurance-history-parent">
            {t(
              'PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.PREVIOUS_OTHER_INSURANCE_TITLE'
            )}
          </div>
          <GridTable
            tableRef={pastInsuranceTableRef}
            columns={columns}
            data={otherInsurances}
            options={{
              ...DEFAULT_GRID_TABLE__OPTIONS,
              search: false,
              showTitle: false,
              toolbar: false,
              sorting: true,
              paging: true,
              emptyRowsWhenPaging: false,
              pageSize: PAGINATION_SIZE.ACTION_HISTORY,
              pageSizeOptions: CUSTOM_PAGINATION_SIZE_OPTIONS.ACTION_HISTORY,
              showEmptyDataSourceMessage: true,
              maxBodyHeight: '100%',
              tableLayout: 'auto',
              selection: false,
              draggable: false,
              headerStyle: {
                color: DEFAULT_BLACK_COLOR,
                fontFamily: APP.FONT_FAMILY.MEDIUM,
                flexDirection: 'row'
              }
            }}
            localization={{
              body: {
                emptyDataSourceMessage: t(
                  'PATIENT_DETAILS_DRAWER.INSURANCE_MODULE.NO_PAST_INSURANCE'
                )
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              boxShadow:
                '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
              flexShrink: 0,
              maxHeight: '30rem'
            }}
          />
          {viewInsuranceDialogOpen && (
            <ViewInsuranceImagesCardsDialog
              isOpen={viewInsuranceDialogOpen}
              handleClose={closeViewInsuranceCardsDialog}
              selectedInsuranceData={selectedInsurance}
              fromEdit={fromEdit}
              onUpdateSuccess={() => {
                fetchPatientInsuranceHistory();
              }}
            />
          )}
          {addEditInsuranceModalOpen &&
            patientDetails?.patient_id &&
            groupId && (
              <VerifyInsuranceModal
                key={`add-edit-insurance-${addEditMode}-${
                  addEditInsuranceInitialValues?.id ?? 'new'
                }`}
                isOpen={addEditInsuranceModalOpen}
                handleClose={closeAddEditInsuranceModal}
                clinicGroupId={groupId}
                clinicId={clinicId ?? ''}
                patientId={patientDetails.patient_id}
                onCancel={closeAddEditInsuranceModal}
                defaultInsuranceValues={addEditInsuranceInitialValues}
                mode={addEditMode}
                onSuccess={() => fetchPatientInsuranceHistory()}
              />
            )}
        </>
      )}
    </div>
  );
};

export default InsuranceHistoryContainer;
